/**
 * Codes HTTP qui relèvent du fonctionnement normal, et non d'une défaillance.
 *
 * Le quota Bugsnag du compte est de 250 événements par jour. Sans ce filtre,
 * les seuls 401 d'authentification en produisaient près de 6 800 — le quota
 * était saturé en permanence et les vraies erreurs jetées avant d'être vues.
 *
 * Il ne s'agit donc pas de masquer des erreurs, mais d'écarter ce qui n'en est
 * pas :
 *
 * - 401 : requête sans session valide. C'est la réponse attendue pour un
 *   visiteur non connecté ou une session expirée.
 * - 403 : session valide mais droits insuffisants. Réponse attendue, elle
 *   aussi : le contrôle d'accès fait son travail.
 * - 404 : route inexistante. Le hook Nitro reçoit ici les 404 de toutes les
 *   requêtes non appariées, y compris celles des scanners automatiques
 *   (`/.env`, `/wp-login.php`…) — du bruit de fond permanent. Un mauvais appel
 *   d'API interne reste visible côté appelant, dont l'erreur, elle, remonte.
 *
 * Tout le reste continue de remonter, `handled` compris : une exception
 * rattrapée n'est pas forcément sans intérêt, c'est le code HTTP attendu qui
 * distingue le normal de l'anormal.
 */
export const CODES_HTTP_ATTENDUS = [401, 403, 404] as const

const codesAttendus = new Set<number>(CODES_HTTP_ATTENDUS)

/**
 * Lit le code HTTP d'une erreur.
 *
 * Seules les `H3Error` issues de `createError()` — et les `FetchError` de
 * `$fetch` — en portent un. Une exception ordinaire n'en a pas : elle renvoie
 * `undefined`.
 *
 * `statusCode` est la propriété de Nuxt et de h3 ; `status` celle de l'API
 * `Response` du navigateur, que portent aussi les `FetchError` d'ofetch. Le
 * filtre sert désormais les deux côtés — Nitro et application —, d'où la
 * lecture des deux propriétés, la première trouvée l'emportant.
 */
export function statusCodeDeLErreur(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined

  const candidat = error as { statusCode?: unknown, status?: unknown }

  if (typeof candidat.statusCode === 'number') return candidat.statusCode
  if (typeof candidat.status === 'number') return candidat.status

  return undefined
}

/**
 * Décide si une erreur doit être envoyée à Bugsnag.
 *
 * Par défaut oui : une erreur dont on ne sait rien — pas de `statusCode`, code
 * inattendu — doit remonter. Seuls les codes explicitement listés comme
 * attendus sont écartés.
 */
export function doitEtreNotifiee(error: unknown): boolean {
  const statusCode = statusCodeDeLErreur(error)

  if (statusCode === undefined) return true

  return !codesAttendus.has(statusCode)
}
